import { Badge } from "@hortis/ui/badge";
import { Button, IconButton } from "@hortis/ui/button";
import { colors } from "@hortis/ui/colors";
import { Dialog, DialogClose, DialogTrigger } from "@hortis/ui/dialog";
import { CameraPlus, XClose } from "@hortis/ui/icons";
import { MarkerPinOff1 } from "@hortis/ui/icons/marker-pin-off-1";
import { MarkerPinPlus1 } from "@hortis/ui/icons/marker-pin-plus-1";
import { PhotoPlaceholder } from "@hortis/ui/photo-placeholder";
import { toast } from "@hortis/ui/sonner";
import { useParams, useRouteContext } from "@tanstack/react-router";
import * as turf from "@turf/turf";
import { TwicImg } from "@twicpics/components/react";
import {
  MaterialImageFieldsFragment,
  namedOperations,
} from "generated/graphql";
import { useEffect, useState } from "react";
import { Layer, Source } from "react-map-gl";
import { queryClient } from "src/app";
import { FeaturedIcon } from "src/components/featured-icon/featured-icon";
import { Lightbox, LightboxTrigger } from "src/components/lightbox";
import { LocationPolygonViewingLayer } from "src/components/map/location-bed-polygons/location-bed-polygon-layer";
import { parseLocationsGeometry } from "src/components/map/location-bed-polygons/parse-geometry";
import {
  circleRadiusStyle,
  circleStrokeWidthStyle,
} from "src/components/map/material-clusters/material-cluster-layers";
import { MapOverlay } from "src/components/map/overlays/map-overlay";
import { MapPreviewOverlay } from "src/components/map/overlays/map-preview-overlay";
import { MapDialogContent } from "src/components/map/position-field/map-position-modal";
import { getCollectionLocationGeometryDocument } from "src/features/collection-map/graphql";
import { MapPositionDialog } from "src/features/create-accession/components/add-plant-material-form/map-position-field/map-position-dialog";
import { CreatePhotosDialog } from "src/features/material-page/create-photos-dialog/create-photos-dialog";
import { ControlledMap } from "src/features/records/material/components/material-title-card/controlled-map";
import { updatePlantMaterialDocument } from "src/features/records/material/graphql";
import { useGqlMutation, useGqlQuery } from "src/utils/hooks/graphql";
import { twMerge } from "tailwind-merge";

export const RecordGallerySection = ({
  thumbnail,
  photos,
  mapLocations,
  hideMap = false,
  className,
  grayscale = false,
  materialId,
  canEdit,
}: {
  thumbnail: MaterialImageFieldsFragment | null;
  photos: ReadonlyArray<MaterialImageFieldsFragment> | null;
  mapLocations: Array<{ latitude: number; longitude: number }>;
  hideMap?: boolean;
  className?: string;
  grayscale?: boolean;
  materialId?: string;
  canEdit: boolean;
}) => {
  const [addPositionDialogOpen, setAddPositionDialogOpen] = useState(false);
  const [mapView, setMapView] = useState<
    | undefined
    | {
        bounds: [[number, number], [number, number]];
        latitude: number;
        longitude: number;
        zoom: number;
      }
  >(undefined);

  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ strict: false });
  const { data: locationGeometryData, error: locationGeometryError } =
    useGqlQuery({
      query: getCollectionLocationGeometryDocument,
      variables: {
        organisationSubdomain: subdomain,
        collectionSiteSlug: siteSlug ?? "",
      },
      enabled: siteSlug != null,
    });
  const locationGeometry = parseLocationsGeometry(
    locationGeometryData?.org?.site?.locationsGeometry,
  );

  const updateMaterial = useGqlMutation({
    mutation: updatePlantMaterialDocument,
    onError: () => {
      toast.error("Failed to add position");
    },
    onSuccess: (res) => {
      if (res.updatePlantMaterial?.__typename !== "PlantMaterial") {
        toast.error("Failed to add position");
      }
      toast.success("Position added");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterial],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterialById],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.MapListMaterials],
      });
      setAddPositionDialogOpen(false);
    },
  });

  useEffect(() => {
    if (mapLocations.length > 0) {
      const featureCollection = turf.featureCollection(
        mapLocations.map((coord) =>
          turf.point([coord.longitude, coord.latitude]),
        ),
      );
      const center = turf.center(featureCollection).geometry.coordinates;
      const bbox = turf.bbox(featureCollection);

      setMapView({
        bounds: [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        longitude: center[0] ?? 0,
        latitude: center[1] ?? 0,
        zoom: 18,
      });
    }
  }, [mapLocations]);

  return (
    <div className={twMerge("flex gap-2 group", className)}>
      <div className="grid flex-1 gap-2 max-h-56">
        {thumbnail == null && canEdit && materialId != null ? (
          <div
            className="relative flex-1 bg-grey-50 flex items-center justify-center rounded-xl border border-grey-100 aspect-16/10 h-auto p-4 max-h-56"
            data-cy="photo-empty-state"
          >
            <CreatePhotosDialog materialId={materialId}>
              <DialogTrigger asChild>
                <Button
                  size="xs"
                  startIcon={CameraPlus}
                  className="rounded-full border-grey-200 shadow-none"
                  testId="add-photos-button"
                >
                  Add photos
                </Button>
              </DialogTrigger>
            </CreatePhotosDialog>
          </div>
        ) : thumbnail == null ? (
          <PhotoPlaceholder
            size="lg"
            className="flex-1 w-full self-stretch h-auto aspect-16/10 max-h-56"
            data-cy="photo-empty-state"
          />
        ) : (
          <div className="relative flex-1 max-h-56">
            <Lightbox photo={thumbnail} photos={photos}>
              <LightboxTrigger
                className={twMerge(
                  "ring-grey-300 outline-hidden h-full hover:ring-2 rounded-xl ring-offset-2 focus-visible:ring-2 flex-1 transition relative w-full",
                  grayscale && "grayscale",
                )}
                data-cy="photo-card"
              >
                <TwicImg
                  src={`${thumbnail.bucketName}/${thumbnail.fileName}`}
                  className="shrink-0 overflow-hidden rounded-xl h-full [&_.twic-w]:h-full"
                  ratio={16 / 10}
                />
                {photos != null && photos.length > 1 && (
                  <Badge
                    className="absolute right-2 bottom-2 bg-white/80 shadow-lg backdrop-blur-md text-grey-600 mix-blend-normal rounded-lg text-sm"
                    color="grey"
                    testId="material-photos-count"
                  >
                    +{photos?.length}
                  </Badge>
                )}
              </LightboxTrigger>
              {materialId != null && canEdit && (
                <CreatePhotosDialog materialId={materialId}>
                  <DialogTrigger
                    asChild
                    className="left-2 bottom-2 absolute opacity-0 group-hover:opacity-100 transition"
                  >
                    <IconButton
                      size="sm"
                      ariaLabel="Upload photos"
                      icon={CameraPlus}
                      variant="tertiaryGray"
                      className="bg-white"
                      testId="add-photos-button"
                    />
                  </DialogTrigger>
                </CreatePhotosDialog>
              )}
            </Lightbox>
          </div>
        )}
      </div>
      {hideMap ? null : mapLocations.length === 0 &&
        materialId != null &&
        canEdit ? (
        <MapPositionDialog
          value={null}
          onChange={(position) => {
            updateMaterial.mutateAsync({
              materialId,
              updates: { position: position },
            });
          }}
          open={addPositionDialogOpen}
          onOpenChange={setAddPositionDialogOpen}
        >
          <div className="relative flex-1 aspect-16/10">
            <ControlledMap
              useSiteCoordinates
              latitude={0}
              longitude={0}
              staticMap
            />
            <MapPreviewOverlay blur>
              <Button
                size="xs"
                startIcon={MarkerPinPlus1}
                className="rounded-full border-grey-200 shadow-none"
                onClick={() => {
                  setAddPositionDialogOpen(true);
                }}
                testId="add-position-button"
              >
                Add position
              </Button>
            </MapPreviewOverlay>
          </div>
        </MapPositionDialog>
      ) : mapLocations.length === 0 ? (
        <div className="relative flex-1 aspect-16/10" data-cy="map-empty-state">
          <ControlledMap
            useSiteCoordinates
            latitude={0}
            longitude={0}
            staticMap
          />
          <MapPreviewOverlay blur>
            <FeaturedIcon color="gray">
              <MarkerPinOff1 className="text-grey-400" />
            </FeaturedIcon>
          </MapPreviewOverlay>
        </div>
      ) : (
        <Dialog>
          <DialogTrigger asChild>
            <div
              className={twMerge(
                "flex-1 ring-grey-300 aspect-16/10 outline-hidden hover:ring-2 rounded-xl ring-offset-2 focus-visible:ring-2 transition cursor-pointer flex justify-center items-center relative",
                grayscale && "grayscale",
              )}
              data-cy="record-map"
            >
              {mapView != null && (
                <ControlledMap
                  cursor="pointer"
                  mapId="thumbnailMap"
                  staticMap
                  viewState={
                    mapLocations.length === 1
                      ? {
                          longitude: mapView.longitude,
                          latitude: mapView.latitude,
                          zoom: mapView.zoom,
                        }
                      : undefined
                  }
                  mapProps={
                    mapLocations.length !== 1
                      ? {
                          bounds: mapView.bounds,
                          fitBoundsOptions: {
                            padding: 50,
                          },
                        }
                      : undefined
                  }
                >
                  {locationGeometry != null && !locationGeometryError && (
                    <LocationPolygonViewingLayer geometry={locationGeometry} />
                  )}
                  <Source
                    id="unclustered-point"
                    type="geojson"
                    data={{
                      type: "FeatureCollection",
                      features: mapLocations.map(({ latitude, longitude }) => ({
                        type: "Feature",
                        geometry: {
                          type: "Point",
                          coordinates: [longitude, latitude],
                        },
                        properties: {},
                      })),
                    }}
                  >
                    <Layer
                      id="unclustered-point"
                      type="circle"
                      source="unclustered-point"
                      filter={["!", ["has", "point_count"]]}
                      paint={{
                        "circle-opacity": 1,
                        "circle-stroke-opacity": 0.9,
                        "circle-color": colors.primary[600],
                        "circle-radius": circleRadiusStyle,
                        "circle-stroke-width": circleStrokeWidthStyle,
                        "circle-stroke-color": colors.white,
                      }}
                    />
                  </Source>
                </ControlledMap>
              )}
              <Badge
                className="absolute right-2 bottom-2 bg-white/80 shadow-lg backdrop-blur-md text-grey-600 mix-blend-normal rounded-lg text-sm"
                color="grey"
              >
                {mapLocations.length === 1
                  ? `${mapLocations[0]?.latitude.toFixed(3)}, ${mapLocations[0]?.longitude.toFixed(3)}`
                  : `${mapLocations.length} positioned`}
              </Badge>
            </div>
          </DialogTrigger>
          <MapDialogContent
            {...(mapLocations.length === 1 && mapView != null
              ? {
                  longitude: mapView.longitude,
                  latitude: mapView.latitude,
                  zoom: mapView.zoom,
                }
              : {
                  bounds: mapView?.bounds,
                  fitBoundsOptions: {
                    padding: 200,
                  },
                })}
          >
            <MapOverlay className="flex items-center justify-center">
              <Source
                id="map-geometry-2"
                type="geojson"
                data={{
                  type: "FeatureCollection",
                  features: mapLocations.map(({ latitude, longitude }) => ({
                    type: "Feature",
                    geometry: {
                      type: "Point",
                      coordinates: [longitude, latitude],
                    },
                    properties: {},
                  })),
                }}
              >
                <Layer
                  id="unclustered-point"
                  type="circle"
                  source="map-geometry-2"
                  filter={["!", ["has", "point_count"]]}
                  paint={{
                    "circle-opacity": 1,
                    "circle-stroke-opacity": 0.9,
                    "circle-color": colors.primary[600],
                    "circle-radius": circleRadiusStyle,
                    "circle-stroke-width": circleStrokeWidthStyle,
                    "circle-stroke-color": colors.white,
                  }}
                />
                {locationGeometry != null && !locationGeometryError && (
                  <LocationPolygonViewingLayer geometry={locationGeometry} />
                )}
              </Source>
              <div className="pointer-events-none absolute right-4 top-4">
                <DialogClose asChild>
                  <IconButton
                    icon={XClose}
                    ariaLabel="Close map"
                    className="border-transparent p-2 shadow-md md:p-2.5"
                    testId="close-map-button"
                    size="sm"
                  />
                </DialogClose>
              </div>
            </MapOverlay>
          </MapDialogContent>
        </Dialog>
      )}
    </div>
  );
};
